// src/components/Procurement/VendorForecastAlerts.jsx

import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { ChevronDown, ChevronUp, TrendingDown, TrendingUp } from 'lucide-react';
import { useUserPermissions } from '@/hooks/useUserPermissions';

const alertColors = {
  low: 'text-green-400 border-green-500',
  medium: 'text-yellow-400 border-yellow-500',
  high: 'text-red-500 border-red-600',
};

const VendorForecastAlerts = () => {
  const { canViewVendorForecasts } = useUserPermissions();
  const [alerts, setAlerts] = useState([]);
  const [expanded, setExpanded] = useState({});
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!canViewVendorForecasts) return;

    const fetchAlerts = async () => {
      setLoading(true);
      setError(null);
      try {
        const res = await axios.get('/solo-mdsh-api/vendors/forecast-alerts');
        setAlerts(Array.isArray(res.data?.data) ? res.data.data : []);
      } catch (err) {
        console.error('Failed to fetch vendor forecast alerts:', err);
        setError('Failed to load forecast alerts.');
      } finally {
        setLoading(false);
      }
    };

    fetchAlerts();
  }, [canViewVendorForecasts]);

  if (!canViewVendorForecasts) {
    return (
      <div className="p-6 text-red-400">
        <h2 className="text-xl font-bold mb-2">🚫 Restricted Access</h2>
        <p>You do not have permission to view vendor forecast insights.</p>
      </div>
    );
  }

  const toggleExpand = (vendorId) => {
    setExpanded((prev) => ({ ...prev, [vendorId]: !prev[vendorId] }));
  };

  return (
    <div className="bg-white/5 backdrop-blur-md rounded-xl p-6 border border-white/10 shadow-lg">
      <h2 className="text-lg font-bold text-white mb-4">📊 Vendor Forecast Alerts</h2>

      {loading ? (
        <p className="text-sm text-blue-300">🔄 Loading vendor forecast alerts...</p>
      ) : error ? (
        <p className="text-sm text-red-400">⚠️ {error}</p>
      ) : alerts.length === 0 ? (
        <p className="text-sm text-gray-400">No alerts available.</p>
      ) : (
        <div className="space-y-4">
          {alerts.map((alert) => (
            <div key={alert.vendor_id} className={`p-4 border rounded-lg ${alertColors[alert.alert_level] || 'text-white border-slate-500'} bg-black/30`}>
              <div className="flex justify-between items-center">
                <div>
                  <p className="text-white font-semibold">{alert.vendor_name}</p>
                  <p className="text-sm text-slate-300">
                    Latest Score:{' '}
                    {typeof alert.current_score === 'number' ? alert.current_score.toFixed(2) : 'N/A'} / Avg:{' '}
                    {typeof alert.rolling_avg_score === 'number' ? alert.rolling_avg_score.toFixed(2) : 'N/A'}
                  </p>
                  <p className="text-sm italic text-sky-300">{alert.ai_insight_summary || 'No insights provided.'}</p>
                  <p className="text-xs text-slate-400 mt-1">
                    Last Evaluated:{' '}
                    {alert.last_evaluated
                      ? new Date(alert.last_evaluated).toLocaleDateString()
                      : 'Unknown'}
                  </p>
                </div>
                <div className="flex items-center space-x-2">
                  {alert.trend_direction === 'up' ? (
                    <TrendingUp className="text-green-400" />
                  ) : (
                    <TrendingDown className="text-red-400" />
                  )}
                  <button onClick={() => toggleExpand(alert.vendor_id)} className="text-slate-200 hover:text-white">
                    {expanded[alert.vendor_id] ? <ChevronUp /> : <ChevronDown />}
                  </button>
                </div>
              </div>

              {expanded[alert.vendor_id] && (
                <div className="mt-4 text-sm">
                  <p className="text-slate-200 font-semibold mb-2">Last 18 Score History:</p>
                  <div className="flex flex-wrap gap-2">
                    {(Array.isArray(alert.last_18_scores) ? alert.last_18_scores : []).map((score, index) => (
                      <span
                        key={index}
                        className="px-2 py-1 text-xs rounded bg-white/10 border border-white/10 text-white"
                      >
                        {typeof score === 'number' ? score.toFixed(2) : 'N/A'}
                      </span>
                    ))}
                  </div>
                </div>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default VendorForecastAlerts;
